import React, { FC } from 'react';

interface AuthLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Modern Auth Layout with gradient background, glass morphism card, and animations
 */
const AuthLayout: FC<AuthLayoutProps> = ({ children, title, subtitle }) => (
  <div className='min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden'>
    {/* Animated gradient background */}
    <div className='absolute inset-0 bg-gradient-to-br from-primary-500 via-primary-600 to-emerald-700' />
    
    {/* Decorative circles */}
    <div className='absolute top-0 left-0 w-96 h-96 bg-primary-400/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2' />
    <div className='absolute bottom-0 right-0 w-96 h-96 bg-emerald-400/30 rounded-full blur-3xl translate-x-1/2 translate-y-1/2' />
    <div className='absolute top-1/2 left-1/2 w-64 h-64 bg-white/10 rounded-full blur-2xl -translate-x-1/2 -translate-y-1/2' />
    
    {/* Subtle grid pattern overlay */}
    <div 
      className='absolute inset-0 opacity-[0.03]'
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }}
    />

    {/* Auth Card */}
    <div className='relative w-full max-w-md animate-fade-in-up'>
      {/* Glass card */}
      <div className='bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden'>
        {/* Card header with logo */}
        <div className='pt-8 pb-4 px-8 text-center'>
          {/* Logo */}
          <div className='flex justify-center mb-6'>
            <div className='relative'>
              <div className='absolute inset-0 bg-primary-500/20 rounded-full blur-xl scale-150' />
              <img 
                src='/assets/Dav-safaris-transparent-logo.png' 
                alt='Dav Safaris' 
                className='relative h-20 w-auto object-contain'
              />
            </div>
          </div>
          
          {/* Title */}
          {title && (
            <h1 className='text-3xl font-bold text-gray-900 dark:text-white mb-2 font-heading'>
              {title}
            </h1>
          )}
          
          {/* Subtitle */}
          {subtitle && (
            <p className='text-gray-500 dark:text-gray-400 text-sm'>
              {subtitle}
            </p>
          )}
        </div>

        {/* Card body */}
        <div className='px-8 pb-8'>
          {children}
        </div>
      </div>

      {/* Footer text */}
      <div className='mt-6 text-center'>
        <p className='text-white/60 text-sm'>
          © {new Date().getFullYear()} Dav Safaris. All rights reserved.
        </p>
      </div>
    </div>
  </div>
);

export default AuthLayout;
